// ConsultantsSystem.js - Hire specialists that provide persistent gameplay bonuses
// Each consultant has upfront cost + monthly retainer + specific abilities

// ============================================
// CONSULTANT TYPES
// ============================================

export const CONSULTANT_TYPES = {
  NEGOTIATOR: {
    id: 'negotiator',
    title: 'Negotiator',
    description: 'Expert in airport slot negotiations. Improves your odds in competitive bidding.',
    hireCost: 500000 * 100,       // $500K upfront
    monthlyCost: 50000 * 100,     // $50K/month
    effects: {
      negotiationBonus: 5,         // +5 to all negotiation rolls
      relationGainMultiplier: 1.2, // 20% more relation gains from successful deals
    },
    requirements: {
      minTier: 1,
      minCash: 1000000 * 100,
    },
  },

  LAWYER: {
    id: 'lawyer',
    title: 'Aviation Lawyer',
    description: 'Reduces penalties from slot violations and disputes. Can challenge unfair rulings.',
    hireCost: 750000 * 100,       // $750K upfront
    monthlyCost: 75000 * 100,     // $75K/month
    effects: {
      violationPenaltyReduction: 0.5, // 50% reduction in violation penalties
      disputeWinChance: 0.3,          // 30% chance to overturn negative rulings
      contractProtection: true,        // Protects against predatory slot terms
    },
    requirements: {
      minTier: 2,
      minCash: 2000000 * 100,
    },
  },

  FIXER: {
    id: 'fixer',
    title: 'Industry Fixer',
    description: 'Connected insider who can repair relations and unlock back-channel deals.',
    hireCost: 1000000 * 100,      // $1M upfront
    monthlyCost: 100000 * 100,    // $100K/month
    effects: {
      relationRepairRate: 2.0,     // 2x faster relation recovery
      backChannelAccess: true,     // Can negotiate with hostile airports
      intelGathering: true,        // Reveals competitor bidding ranges
    },
    requirements: {
      minTier: 3,
      minCash: 3000000 * 100,
    },
  },

  ANALYST: {
    id: 'analyst',
    title: 'Market Analyst',
    description: 'Provides demand forecasting and route profitability insights.',
    hireCost: 300000 * 100,       // $300K upfront
    monthlyCost: 40000 * 100,     // $40K/month
    effects: {
      demandVisibility: true,      // See exact demand numbers before committing
      profitProjections: true,     // Accurate route profit estimates
      competitorTracking: true,    // See competitor route changes
    },
    requirements: {
      minTier: 1,
      minCash: 500000 * 100,
    },
  },

  LOBBYIST: {
    id: 'lobbyist',
    title: 'Airport Lobbyist',
    description: 'Political connections that can influence slot allocations and airport expansions.',
    hireCost: 2000000 * 100,      // $2M upfront
    monthlyCost: 150000 * 100,    // $150K/month
    effects: {
      slotAllocationBonus: 10,     // +10 in competitive slot allocations
      expansionNotice: true,       // Early notice of new slot availability
      regulatoryProtection: true,  // Reduced chance of regulatory actions against you
    },
    requirements: {
      minTier: 4,
      minCash: 5000000 * 100,
    },
  },
};

// ============================================
// CONSULTANT STATE MANAGEMENT
// ============================================

export function createConsultantState() {
  return {
    hired: {},           // { negotiator: { hiredAt, monthsPaid } }
    available: generateAvailableConsultants(),
    totalMonthlyCost: 0,
  };
}

function generateAvailableConsultants() {
  // Generate consultant "candidates" with slight variations
  const available = [];
  
  Object.values(CONSULTANT_TYPES).forEach(type => {
    // Each type has 2-3 candidates with slightly different stats
    const numCandidates = 2 + Math.floor(Math.random() * 2);
    
    for (let i = 0; i < numCandidates; i++) {
      available.push({
        id: `${type.id}-${Date.now()}-${i}`,
        type: type.id,
        name: generateConsultantName(type.id),
        ...type,
        // Slight variations
        hireCost: Math.round(type.hireCost * (0.9 + Math.random() * 0.2)),
        monthlyCost: Math.round(type.monthlyCost * (0.9 + Math.random() * 0.2)),
        reputation: Math.floor(60 + Math.random() * 40), // 60-100
        specialty: getRandomSpecialty(type.id),
      });
    }
  });
  
  return available;
}

function generateConsultantName(type) {
  const firstNames = ['James', 'Sarah', 'Michael', 'Elizabeth', 'Robert', 'Jennifer', 'William', 'Patricia', 'David', 'Linda'];
  const lastNames = ['Morrison', 'Chen', 'Rodriguez', 'Thompson', 'Nakamura', 'Williams', 'Schmidt', 'O\'Brien', 'Patel', 'Kowalski'];
  
  return `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
}

function getRandomSpecialty(type) {
  const specialties = {
    negotiator: ['Slot Acquisition', 'Contract Terms', 'Multi-Party Deals', 'Crisis Negotiation'],
    lawyer: ['FAA Compliance', 'Contract Law', 'Dispute Resolution', 'Regulatory Defense'],
    fixer: ['Airport Relations', 'Competitor Intel', 'Crisis Management', 'Political Connections'],
    analyst: ['Demand Forecasting', 'Route Optimization', 'Competitor Analysis', 'Market Timing'],
    lobbyist: ['FAA Relations', 'Airport Authority', 'Congressional Ties', 'Local Government'],
  };
  
  const opts = specialties[type] || ['General'];
  return opts[Math.floor(Math.random() * opts.length)];
}

// ============================================
// CONSULTANT ACTIONS
// ============================================

export function hireConsultant(state, consultantId, cash) {
  const consultant = state.available.find(c => c.id === consultantId);
  if (!consultant) {
    return { success: false, error: 'Consultant not found' };
  }
  
  // Check if already have this type
  if (state.hired[consultant.type]) {
    return { success: false, error: `Already have a ${consultant.title} on retainer` };
  }
  
  // Check cash
  if (cash < consultant.hireCost) {
    return { success: false, error: 'Insufficient funds for hiring bonus' };
  }
  
  // Hire
  const newState = { ...state };
  newState.hired = {
    ...state.hired,
    [consultant.type]: {
      ...consultant,
      hiredAt: Date.now(),
      monthsPaid: 0,
    },
  };
  
  // Remove from available
  newState.available = state.available.filter(c => c.id !== consultantId);
  
  // Update total monthly cost
  newState.totalMonthlyCost = calculateTotalMonthlyCost(newState.hired);
  
  return { 
    success: true, 
    state: newState,
    cost: consultant.hireCost,
    message: `${consultant.name} is now on retainer as your ${consultant.title}.`,
  };
}

export function fireConsultant(state, consultantType) {
  if (!state.hired[consultantType]) {
    return { success: false, error: 'No consultant of that type on retainer' };
  }
  
  const consultant = state.hired[consultantType];
  
  const newState = { ...state };
  newState.hired = { ...state.hired };
  delete newState.hired[consultantType];
  
  // Add back to available pool (they might come back later)
  newState.available = [...state.available, {
    ...consultant,
    id: `${consultant.type}-${Date.now()}`, // New ID
  }];
  
  newState.totalMonthlyCost = calculateTotalMonthlyCost(newState.hired);
  
  return {
    success: true,
    state: newState,
    message: `${consultant.name} has been released from their contract.`,
  };
}

export function processMonthlyConsultantCosts(state) {
  const costs = [];
  const newHired = { ...state.hired };
  
  Object.entries(state.hired).forEach(([type, consultant]) => {
    costs.push({
      type,
      name: consultant.name,
      cost: consultant.monthlyCost,
    });
    newHired[type] = {
      ...consultant,
      monthsPaid: consultant.monthsPaid + 1,
    };
  });
  
  return {
    state: { ...state, hired: newHired },
    totalCost: state.totalMonthlyCost,
    breakdown: costs,
  };
}

function calculateTotalMonthlyCost(hired) {
  return Object.values(hired).reduce((sum, c) => sum + c.monthlyCost, 0);
}

// ============================================
// EFFECT CHECKERS - Use these in game systems
// ============================================

export function hasConsultant(consultantState, type) {
  return !!consultantState?.hired?.[type];
}

export function getConsultantEffect(consultantState, type, effectName) {
  const consultant = consultantState?.hired?.[type];
  if (!consultant) return null;
  return consultant.effects?.[effectName];
}

export function getNegotiationBonus(consultantState) {
  let bonus = 0;
  
  // Negotiator bonus
  const negotiatorBonus = getConsultantEffect(consultantState, 'negotiator', 'negotiationBonus');
  if (negotiatorBonus) bonus += negotiatorBonus;
  
  // Lobbyist bonus
  const lobbyistBonus = getConsultantEffect(consultantState, 'lobbyist', 'slotAllocationBonus');
  if (lobbyistBonus) bonus += lobbyistBonus;
  
  return bonus;
}

export function canAccessBackChannels(consultantState) {
  return getConsultantEffect(consultantState, 'fixer', 'backChannelAccess') === true;
}

export function getRelationRepairMultiplier(consultantState) {
  const rate = getConsultantEffect(consultantState, 'fixer', 'relationRepairRate');
  return rate || 1.0;
}

export function getViolationPenaltyMultiplier(consultantState) {
  const reduction = getConsultantEffect(consultantState, 'lawyer', 'violationPenaltyReduction');
  return reduction ? (1 - reduction) : 1.0;
}

export function hasMarketIntel(consultantState) {
  return hasConsultant(consultantState, 'analyst');
}

export function hasCompetitorIntel(consultantState) {
  // Either analyst or fixer can provide this
  return hasConsultant(consultantState, 'analyst') || 
         getConsultantEffect(consultantState, 'fixer', 'intelGathering') === true;
}

// ============================================
// EXPORTS
// ============================================

export default {
  CONSULTANT_TYPES,
  createConsultantState,
  hireConsultant,
  fireConsultant,
  processMonthlyConsultantCosts,
  hasConsultant,
  getConsultantEffect,
  getNegotiationBonus,
  canAccessBackChannels,
  getRelationRepairMultiplier,
  getViolationPenaltyMultiplier,
  hasMarketIntel,
  hasCompetitorIntel,
};
